import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import api from '../services/api';
import { useOrgCuratedCurrencies } from '../hooks/useOrgCuratedCurrencies';
import { formatCurrencyPickerLabel } from '../utils/iso4217';
import { formatMoneyAmount } from '../utils/formatMoney';
import type {
  AccountBudgetDto,
  AccountBudgetProgressDto,
  BudgetHoursScope,
  BudgetPeriodKind,
  BudgetRecurrence,
  BudgetTargetMode,
  BudgetType,
  UpsertAccountBudgetBody
} from '../types/accountBudget';

const BUDGET_TYPES: { value: BudgetType; label: string }[] = [
  { value: 'HOURS', label: 'Hours' },
  { value: 'INTERNAL_COST', label: 'Internal cost' },
  { value: 'BILLABLE', label: 'Billable' }
];

const PERIOD_KINDS: { value: BudgetPeriodKind; label: string }[] = [
  { value: 'ONE_OFF', label: 'One-off (custom range)' },
  { value: 'RECURRING', label: 'Recurring' }
];

const RECURRENCES: { value: BudgetRecurrence; label: string }[] = [
  { value: 'WEEK', label: 'Week (ISO)' },
  { value: 'MONTH', label: 'Month' },
  { value: 'YEAR', label: 'Year' }
];

function progressValue(registered: number, target: number): number {
  if (target <= 0) return registered > 0 ? 100 : 0;
  return Math.min(100, (registered / target) * 100);
}

function formatRegistered(progress: AccountBudgetProgressDto): string {
  if (progress.type === 'HOURS') {
    return `${progress.registered.toFixed(2)} h`;
  }
  return formatMoneyAmount(progress.registered, progress.currencyCode ?? '');
}

function formatTarget(progress: AccountBudgetProgressDto): string {
  if (progress.type === 'HOURS') {
    return `${progress.target.toFixed(2)} h`;
  }
  return formatMoneyAmount(progress.target, progress.currencyCode ?? '');
}

interface BudgetFormState {
  type: BudgetType;
  targetAmount: string;
  currencyCode: string;
  periodKind: BudgetPeriodKind;
  recurrence: BudgetRecurrence;
  startDate: string;
  endDate: string;
  hoursScope: BudgetHoursScope;
  targetMode: BudgetTargetMode;
  isActive: boolean;
  showEconomyToMembers: boolean;
  timeZone: string;
}

const defaultForm = (currency: string): BudgetFormState => ({
  type: 'HOURS',
  targetAmount: '',
  currencyCode: currency,
  periodKind: 'RECURRING',
  recurrence: 'MONTH',
  startDate: '',
  endDate: '',
  hoursScope: 'ALL',
  targetMode: 'AGGREGATE',
  isActive: true,
  showEconomyToMembers: false,
  timeZone: ''
});

interface AccountBudgetCardProps {
  accountId: string;
  accountCurrency: string;
  organizationId: string;
  canEdit: boolean;
  onBudgetUpdated?: () => void;
}

export default function AccountBudgetCard({
  accountId,
  accountCurrency,
  organizationId,
  canEdit,
  onBudgetUpdated
}: AccountBudgetCardProps) {
  const { curatedCodes } = useOrgCuratedCurrencies(organizationId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [budget, setBudget] = useState<AccountBudgetDto | null>(null);
  const [progress, setProgress] = useState<AccountBudgetProgressDto | null>(null);
  const [showEconomyToMembers, setShowEconomyToMembers] = useState(false);
  const [effectiveTimeZone, setEffectiveTimeZone] = useState('UTC');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<BudgetFormState>(() => defaultForm(accountCurrency));

  const loadBudget = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [configRes, progressRes] = await Promise.all([
        api.getAccountBudget(accountId),
        api.getAccountBudgetProgress(accountId)
      ]);

      if (configRes.success) {
        const data = configRes.data;
        setShowEconomyToMembers(Boolean(data.showEconomyToMembers));
        setEffectiveTimeZone(data.effectiveTimeZone ?? 'UTC');
        setBudget(data.budget);

        if (data.budget) {
          setForm({
            type: data.budget.type,
            targetAmount: data.budget.targetAmount,
            currencyCode: data.budget.currencyCode ?? accountCurrency,
            periodKind: data.budget.periodKind,
            recurrence: data.budget.recurrence ?? 'MONTH',
            startDate: data.budget.startDate ?? '',
            endDate: data.budget.endDate ?? '',
            hoursScope: data.budget.hoursScope,
            targetMode: data.budget.targetMode ?? 'AGGREGATE',
            isActive: data.budget.isActive,
            showEconomyToMembers: Boolean(data.showEconomyToMembers),
            timeZone: data.timeZone ?? ''
          });
        } else {
          setForm(defaultForm(accountCurrency));
        }
      }

      if (progressRes.success && progressRes.data.hasBudget) {
        setProgress(progressRes.data.progress);
      } else {
        setProgress(null);
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message ?? 'Failed to load budget');
    } finally {
      setLoading(false);
    }
  }, [accountId, accountCurrency]);

  const notifyBudgetUpdated = useCallback(async () => {
    await loadBudget();
    onBudgetUpdated?.();
  }, [loadBudget, onBudgetUpdated]);

  useEffect(() => {
    loadBudget();
  }, [loadBudget]);

  const handleOpenDialog = () => {
    if (budget) {
      setForm({
        type: budget.type,
        targetAmount: budget.targetAmount,
        currencyCode: budget.currencyCode ?? accountCurrency,
        periodKind: budget.periodKind,
        recurrence: budget.recurrence ?? 'MONTH',
        startDate: budget.startDate ?? '',
        endDate: budget.endDate ?? '',
        hoursScope: budget.hoursScope,
        targetMode: budget.targetMode ?? 'AGGREGATE',
        isActive: budget.isActive,
        showEconomyToMembers,
        timeZone: form.timeZone
      });
    } else {
      setForm({ ...defaultForm(accountCurrency), showEconomyToMembers });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const body: UpsertAccountBudgetBody = {
        type: form.type,
        targetAmount: form.targetMode === 'PER_TASK' ? (budget?.targetAmount ?? '0') : form.targetAmount,
        periodKind: form.periodKind,
        currencyCode: form.type === 'HOURS' ? null : form.currencyCode,
        hoursScope: form.type === 'HOURS' ? form.hoursScope : undefined,
        targetMode: form.targetMode,
        recurrence: form.periodKind === 'RECURRING' ? form.recurrence : null,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        isActive: form.isActive,
        showEconomyToMembers: form.showEconomyToMembers,
        timeZone: form.timeZone.trim() || null
      };

      const res = await api.upsertAccountBudget(accountId, body);
      if (!res.success) {
        setError(res.message ?? 'Failed to save budget');
        return;
      }

      setDialogOpen(false);
      await notifyBudgetUpdated();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message ?? 'Failed to save budget');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove budget from this account?')) return;
    try {
      setSaving(true);
      await api.deleteAccountBudget(accountId);
      setDialogOpen(false);
      await notifyBudgetUpdated();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message ?? 'Failed to delete budget');
    } finally {
      setSaving(false);
    }
  };

  const isMoneyType = form.type === 'INTERNAL_COST' || form.type === 'BILLABLE';

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Budget</Typography>
          {canEdit && (
            <Button size="small" startIcon={<EditIcon />} onClick={handleOpenDialog}>
              {budget ? 'Edit' : 'Configure'}
            </Button>
          )}
        </Box>
        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <LinearProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !budget || !progress?.period.active ? (
          <Typography variant="body2" color="text.secondary">
            {budget ? 'No active budget period.' : 'No budget configured.'}
          </Typography>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {progress.typeLabel} · {progress.period.label} ({effectiveTimeZone})
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">
                {formatRegistered(progress)} / {formatTarget(progress)}
              </Typography>
              <Typography variant="body2" color={progress.percent > 100 ? 'error.main' : 'text.secondary'}>
                {progress.percent.toFixed(0)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressValue(progress.registered, progress.target)}
              color={progress.percent > 100 ? 'error' : 'primary'}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{budget ? 'Edit budget' : 'Configure budget'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Budget type</InputLabel>
              <Select
                value={form.type}
                label="Budget type"
                onChange={(e) => setForm({ ...form, type: e.target.value as BudgetType })}
              >
                {BUDGET_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Target mode</InputLabel>
              <Select
                value={form.targetMode}
                label="Target mode"
                onChange={(e) => setForm({ ...form, targetMode: e.target.value as BudgetTargetMode })}
              >
                <MenuItem value="AGGREGATE">Total (single account target)</MenuItem>
                <MenuItem value="PER_TASK">Per task (set targets in task list)</MenuItem>
              </Select>
            </FormControl>

            {form.targetMode === 'AGGREGATE' && (
              <TextField
                label="Target"
                type="number"
                value={form.targetAmount}
                onChange={(e) => setForm({ ...form, targetAmount: e.target.value })}
                fullWidth
                helperText={form.type === 'HOURS' ? 'Hours cap for the period' : 'Money target in account currency'}
              />
            )}

            {form.targetMode === 'PER_TASK' && (
              <Typography variant="body2" color="text.secondary">
                Per-task targets are edited in the tasks table on this account dashboard.
              </Typography>
            )}

            {isMoneyType && (
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={form.currencyCode}
                  label="Currency"
                  onChange={(e) => setForm({ ...form, currencyCode: e.target.value })}
                  disabled={curatedCodes.length === 0}
                >
                  {curatedCodes.map((code) => (
                    <MenuItem key={code} value={code}>
                      {formatCurrencyPickerLabel(code)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {form.type === 'HOURS' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={form.hoursScope === 'BILLABLE_ONLY'}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        hoursScope: e.target.checked ? 'BILLABLE_ONLY' : 'ALL'
                      })
                    }
                  />
                }
                label="Count billable tasks only"
              />
            )}

            <FormControl fullWidth>
              <InputLabel>Period</InputLabel>
              <Select
                value={form.periodKind}
                label="Period"
                onChange={(e) => setForm({ ...form, periodKind: e.target.value as BudgetPeriodKind })}
              >
                {PERIOD_KINDS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {form.periodKind === 'RECURRING' ? (
              <FormControl fullWidth>
                <InputLabel>Recurrence</InputLabel>
                <Select
                  value={form.recurrence}
                  label="Recurrence"
                  onChange={(e) => setForm({ ...form, recurrence: e.target.value as BudgetRecurrence })}
                >
                  {RECURRENCES.map((r) => (
                    <MenuItem key={r.value} value={r.value}>
                      {r.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <>
                <TextField
                  label="Start date"
                  type="date"
                  value={form.startDate}
                  onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="End date"
                  type="date"
                  value={form.endDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </>
            )}

            {form.periodKind === 'RECURRING' && (
              <TextField
                label="Applies from (optional)"
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                helperText="Budget begins on this date; leave empty for immediate start"
              />
            )}

            <TextField
              label="Account timezone (optional)"
              value={form.timeZone}
              onChange={(e) => setForm({ ...form, timeZone: e.target.value })}
              fullWidth
              placeholder="Inherit from organization"
              helperText={`Effective: ${form.timeZone.trim() || effectiveTimeZone}`}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={form.showEconomyToMembers}
                  onChange={(e) => setForm({ ...form, showEconomyToMembers: e.target.checked })}
                />
              }
              label="Show economy to members"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
              }
              label="Budget active"
            />

            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          {budget && (
            <Button color="error" onClick={handleDelete} disabled={saving}>
              Remove budget
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
